import { useIsomorphicLayoutEffect } from "@/hooks/Custom";
import { useAsyncSuggestions } from "@/hooks/Search/useAsyncSuggestions";
import { SuggestionObject } from "@/types/Shared/Search";
import {
  Box,
  Center,
  Flex,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  Spinner,
  useBoolean,
  useBreakpointValue
} from "@chakra-ui/react";
import { Dispatch, SetStateAction, useState } from "react";
import { SmartSearchListDesktop, SmartSearchListMobile } from "./SmartSearchList";

interface SmartSearchProps extends PopoverProps {
  Search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  closeButtonRef: any;
  customW?: string;
}

export function SmartSearch({
  Search,
  setSearch,
  children,
  initialFocusRef,
  closeButtonRef,
  customW,
  ...rest
}: SmartSearchProps) {
  // ! Initial States
  const [DebounceFunction, setDebounceFunction] = useState(null);
  const [Suggestions, setSuggestions] = useState<SuggestionObject[]>(null);
  // ! Rullers & Controllers
  const [DisclosureState, setDisclosureState] = useBoolean();
  const [IsSearching, setIsSearching] = useState<boolean>(true);
  const IsWide = useBreakpointValue({ base: false, lg: true });
  // ! Methods
  async function requestSuggestions() {
    setIsSearching(true);
    const [SuggestData, SuggestError] = await useAsyncSuggestions(Search);
    if (SuggestData && SuggestError === null) {
      setSuggestions(SuggestData);
    } else {
      setSuggestions([]);
    }
    setIsSearching(true);
  }
  // ! Effects
  useIsomorphicLayoutEffect(() => {
    if (Search?.length >= 2) {
      clearTimeout(DebounceFunction);
      setDebounceFunction(setTimeout(() => requestSuggestions(), 500));
      setDisclosureState.on();
    }
    return () => {
      setSuggestions(null);
      setDisclosureState.off();
    };
  }, [Search]);
  // ! Main Return
  return (
    <Flex w={"full"}>
      <Popover
        placement={"bottom-start"}
        isOpen={DisclosureState}
        onClose={setDisclosureState.off}
        initialFocusRef={initialFocusRef}
        {...rest}
      >
        <PopoverTrigger>{children}</PopoverTrigger>
        <PopoverContent
          bgColor={"white"}
          w={"full"}
          borderRadius={"25px"}
          style={{ boxShadow: "#0000002e 2px 5px 10px" }}
          ml={IsWide ? "0px" : "10px"}
        >
          {/*recolocar o ref={closeButtonRef}  */}
          <PopoverCloseButton size={["sm", "lg"]} />
          <PopoverBody minW={"360px"} w={"full"}>
            {Suggestions && IsWide && (
              <SmartSearchListDesktop
                Suggestions={Suggestions}
                setSearch={setSearch}
                setDisclosureState={setDisclosureState}
              />
            )}
            {Suggestions && !IsWide && (
              <SmartSearchListMobile
                Suggestions={Suggestions}
                setSearch={setSearch}
                setDisclosureState={setDisclosureState}
              />
            )}
            {Suggestions === null && (
              <Box h={"300px"} w={"full"}>
                <Center alignItems={"center"} h={"full"}>
                  <Spinner size="xl" thickness={".3rem"} emptyColor={"#C3C3C3"} color={"#1e1e1e"} />
                </Center>
              </Box>
            )}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Flex>
  );
}
